import type { FC, ReactNode } from "react"

import {
  INVERTER_POWUR_OS_EQUIPMENT_ID,
  MODULE_POWUR_OS_EQUIPMENT_ID,
} from "@logic/lib/powur-os"

import { formatters } from "./utils"
import type { SystemPricingItem } from "./utils/system-pricings.utils"

interface PricingTableContainerProps {
  children: ReactNode
}

export const PricingTableContainer: FC<PricingTableContainerProps> = ({
  children,
}) => {
  return (
    <div className="rounded-tl-lg rounded-tr-lg bg-gray-100 p-0.5">
      <div className="max-h-[700px] overflow-y-auto bg-white">
        <table className="w-full table-fixed border-separate border-spacing-y-0">
          {children}
        </table>
      </div>
    </div>
  )
}

export const PricingItemRow: FC<{ item: SystemPricingItem }> = ({ item }) => (
  <tr key={item.id} className="transition-colors hover:bg-gray-50">
    <td
      className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-semibold text-gray-800`}
    >
      {item.name}
    </td>
    <td
      className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm`}
    >
      {formatters.pricePerWatt(item.price)}
    </td>
    <td
      className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800`}
    >
      {formatters.currency(item.total)}
    </td>
  </tr>
)

export const PricingEquipmentItemRow: FC<{
  item: SystemPricingItem
  excludeTotal: boolean
}> = ({ item, excludeTotal }) => {
  const isModuleOrInverter =
    item.categoryId === MODULE_POWUR_OS_EQUIPMENT_ID ||
    item.categoryId === INVERTER_POWUR_OS_EQUIPMENT_ID

  const shouldShowQuantity = isModuleOrInverter && item.qty > 1
  const displayName = shouldShowQuantity
    ? `${item.name} x${item.qty}`
    : item.name

  return (
    <tr key={item.id} className="transition-colors hover:bg-gray-50">
      <td
        className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-semibold text-gray-800`}
      >
        {displayName}
      </td>
      <td
        className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800`}
      >
        {excludeTotal ? `` : formatters.currency(item.total)}
      </td>
    </tr>
  )
}

export const SalesTaxRow: FC<{
  salesTax: number
  systemSizeInWatts: number
}> = ({ salesTax, systemSizeInWatts }) => (
  <tr key="sales-tax" className="bg-white">
    <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
      Sales Tax
    </td>
    <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600">
      {formatters.currency(salesTax / systemSizeInWatts || 0)}
    </td>
    <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
      {formatters.currency(salesTax)}
    </td>
  </tr>
)

export const ExciseTaxRow: FC<{
  exciseTaxRate?: number | null
  exciseTax: number
}> = ({ exciseTaxRate, exciseTax }) => {
  if (!exciseTaxRate) return null

  return (
    <tr key="excise-tax" className="bg-white">
      <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800">
        General Excise Tax
      </td>
      <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600">
        {formatters.percentage(exciseTaxRate)}
      </td>
      <td className="border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800">
        {formatters.currency(exciseTax)}
      </td>
    </tr>
  )
}
